import { useState } from "react";
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Group,
  RegionCargando,
  Skeleton,
  Stack,
  Text,
  Textarea,
} from "@/components/ui";
import { IconSend, IconX } from "@tabler/icons-react";
import dayjs from "dayjs";
import {
  useCancelarSolicitudCatalogo,
  useCrearSolicitudCatalogo,
  useEstadoSolicitudCatalogo,
  useTiposSolicitudCatalogoActivos,
} from "../hooks/use-solicitud-catalogo";
import type { TipoCatalogoSolicitud } from "../services/solicitudes-catalogo.api";
import { PERMISOS } from "@/lib/permisos";
import { useUserPermisos } from "@/lib/auth-store";

export type { TipoCatalogoSolicitud };

/** Qué gana el solicitante cuando se atiende · cierre del aviso «pendiente». */
const AL_ATENDERSE: Record<TipoCatalogoSolicitud, string> = {
  Proveedor: "podrás asignar el proveedor",
  CuentaContablePorClave: "la clave tendrá su cuenta contable",
  ClaveSat: "la clave producto/servicio quedará en el catálogo",
  CentroDeCosto: "podrás elegir el centro de costos",
  CuentaContable: "podrás elegir la cuenta contable",
  EquivalenciaUnidad: "la unidad quedará reconocida",
};

/**
 * Botón + estado de la solicitud de alta de un elemento de catálogo (módulo Solicitudes de
 * catálogo). Se monta dentro del aviso de «no existe en el catálogo». Los textos del botón y
 * del modal los configura el administrador por tipo; si el tipo está INACTIVO no se muestra.
 * Estados: sin solicitud → botón + modal con comentario opcional · Pendiente → aviso +
 * «Cancelar solicitud» · Rechazada → motivo + re-solicitar · Atendida/Cancelada → re-solicitar.
 */
export function SolicitarAltaCatalogo({
  tipo,
  clave,
  numeroSociedad,
  descripcion,
  solicitudPagoId,
  numeroSolicitudPago,
}: {
  tipo: TipoCatalogoSolicitud;
  clave: string;
  /** Obligatorio para CentroDeCosto / CuentaContable (se piden PARA una organización). */
  numeroSociedad?: string;
  descripcion?: string;
  solicitudPagoId?: string;
  numeroSolicitudPago?: number;
}) {
  // Sin permiso de solicitar (p. ej. un autorizador viendo el detalle) no se consulta nada.
  const puedeSolicitar = useUserPermisos().includes(PERMISOS.catalogosSolicitar);
  const { data: tipos, isPending: cargandoTipos } = useTiposSolicitudCatalogoActivos(puedeSolicitar);
  const { data: existente, isPending } = useEstadoSolicitudCatalogo(tipo, clave, numeroSociedad, puedeSolicitar);
  const crear = useCrearSolicitudCatalogo();
  const cancelar = useCancelarSolicitudCatalogo();
  const [modal, setModal] = useState<"solicitar" | "cancelar" | null>(null);
  const [texto, setTexto] = useState("");

  if (!puedeSolicitar) return null;

  // Primera carga · botón skeleton del tamaño del botón `xs` real.
  if (isPending || cargandoTipos) {
    return (
      <RegionCargando etiqueta="Consultando solicitudes de alta…">
        <Skeleton height={28} width={230} aria-hidden="true" />
      </RegionCargando>
    );
  }

  const config = tipos?.find((t) => t.tipo === tipo);
  // Tipo desactivado por el administrador → no se ofrece solicitarlo.
  if (!config) return null;

  const claveVisible = tipo === "EquivalenciaUnidad" ? clave.replace("|", " ↔ ") : clave;
  const cerrar = () => {
    setModal(null);
    setTexto("");
  };

  const enviar = () =>
    crear.mutate(
      {
        tipo,
        clave,
        numeroSociedad,
        descripcion,
        comentario: texto.trim() || undefined,
        solicitudPagoId,
        numeroSolicitudPago,
      },
      { onSuccess: cerrar }
    );

  const confirmarCancelacion = () =>
    existente && cancelar.mutate({ id: existente.id, motivo: texto.trim() || undefined }, { onSuccess: cerrar });

  const dialogo = (
    <Dialog open={modal !== null} onOpenChange={(open) => !open && cerrar()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{modal === "cancelar" ? "Cancelar solicitud" : config.textoBoton}</DialogTitle>
        </DialogHeader>
        <Stack gap="md">
          <Text size="sm">
            {modal === "cancelar" ? (
              <>
                La solicitud de <strong>{claveVisible}</strong> dejará de aparecer en la bandeja de los responsables.
                Podrás volver a solicitarla después.
              </>
            ) : (
              <>
                {config.textoModal.replaceAll("{clave}", claveVisible)}
                {descripcion ? ` (${descripcion})` : ""}
              </>
            )}
          </Text>
          <Textarea
            label={modal === "cancelar" ? "Motivo (opcional)" : "Comentario para los responsables"}
            placeholder={modal === "cancelar" ? "p. ej. Ya no se necesita" : "Opcional · contexto de por qué lo necesitas"}
            value={texto}
            onChange={(e) => setTexto(e.currentTarget.value)}
            maxLength={500}
            rows={2}
          />
        </Stack>
        <DialogFooter>
          <Button variant="default" onClick={cerrar}>
            {modal === "cancelar" ? "Volver" : "Cancelar"}
          </Button>
          {modal === "cancelar" ? (
            <Button className="bg-peligro-600 hover:bg-peligro-700" loading={cancelar.isPending} onClick={confirmarCancelacion}>
              Cancelar solicitud
            </Button>
          ) : (
            <Button loading={crear.isPending} onClick={enviar}>
              Enviar solicitud
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  if (existente?.estado === "Pendiente") {
    return (
      <Stack gap="xs">
        <Text size="sm" fw={500}>
          Alta ya solicitada el {dayjs(existente.creadoAt).format("DD/MM/YYYY HH:mm")} · en cuanto la atiendan{" "}
          {AL_ATENDERSE[tipo]}.
        </Text>
        <Group>
          <Button
            size="xs"
            variant="default"
            leftSection={<IconX size={14} stroke={1.7} />}
            onClick={() => setModal("cancelar")}
          >
            Cancelar solicitud
          </Button>
        </Group>
        {dialogo}
      </Stack>
    );
  }

  return (
    <Stack gap="xs">
      {existente?.estado === "Rechazada" && (
        <Text size="sm">
          <Text span fw={600}>
            Se rechazó una solicitud anterior:
          </Text>{" "}
          {existente.respuestaComentario ?? "sin motivo registrado"}. Puedes volver a solicitarla.
        </Text>
      )}
      {existente?.estado === "Atendida" && (
        <Text size="sm">
          Una solicitud anterior se marcó como atendida. Si el elemento sigue sin aparecer, solicítalo de nuevo.
        </Text>
      )}
      <Group>
        <Button size="xs" leftSection={<IconSend size={14} stroke={1.7} />} onClick={() => setModal("solicitar")}>
          {config.textoBoton}
        </Button>
      </Group>
      {dialogo}
    </Stack>
  );
}
